import { DevToolsButton } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsButton';
import type { DebugInteractionGroupProps } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/DebugTab/DebugInteractionGroup/index.types';
import { DebugLegRow } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/DebugTab/DebugLegRow';
import {
  SURFACE,
  TONE,
} from 'Modules/softphone/components/SoftphoneDevTools/styles';
import { formatTimestamp } from 'Modules/softphone/components/SoftphoneDevTools/utils';
import React from 'react';
import { cn } from '~/lib/utils';

const OUTCOME_INK = {
  success: TONE.ok.text,
  warning: TONE.warn.text,
  error: TONE.bad.text,
  info: 'text-violet-400',
  neutral: TONE.muted.text,
  primary: 'text-emerald-400',
} as const;

/**
 * DebugInteractionGroup - Every leg of one thing that happened
 *
 * A warm transfer is two records and a conference is one per participant
 * plus the room.
 */
export const DebugInteractionGroup: React.FC<DebugInteractionGroupProps> = ({
  interaction,
  onOpenLeg,
  onDownload,
}) => {
  const legCount = interaction.legs.length;

  return (
    <div className={SURFACE.card}>
      <div className="flex items-center gap-2 border-b border-zinc-800 bg-zinc-900 px-3 py-2.5">
        <span
          className={cn(
            'text-[10px] font-bold uppercase tracking-[0.07em]',
            OUTCOME_INK[interaction.outcomeVariant]
          )}
        >
          {interaction.title}
        </span>
        <span className="text-[11px] text-zinc-400">·</span>
        <span className="text-[11px] text-zinc-400">
          {legCount === 1 ? '1 leg' : `${legCount} legs`} · {interaction.outcome}
        </span>
        <div className="flex-1" />
        <span className="font-sp-mono text-[10.5px] text-zinc-400">
          {formatTimestamp(interaction.startedAt, { fallback: '—' })}
        </span>
      </div>

      {interaction.legs.map((leg) => (
        <DebugLegRow key={leg.callId} info={leg} onOpen={() => onOpenLeg(leg)} />
      ))}

      <div className="px-3 py-2">
        <DevToolsButton variant="export" onClick={onDownload}>
          ↓ {legCount > 1 ? `All ${legCount} legs` : 'Download'}
        </DevToolsButton>
      </div>
    </div>
  );
};
